import React from 'react';
import { Card, Badge, Button, Icon, Checkbox } from 'ui';
import { FormatCurrency } from 'utils/features/formatCurrency';
import type { IProposals } from 'api/resources/overviews';
import { EStatus } from 'ui/types/core';

interface IProposalCardProps {
	proposal: IProposals;
	isSelected: boolean;
	onSelect: (proposal: IProposals) => void;
	onOpenPdf: (url: string) => void;
	onOpenModal: (proposal: IProposals) => void;
	onDecline: (proposal: IProposals) => void;
}

export const ProposalCard = ({
	proposal,
	isSelected,
	onSelect,
	onOpenPdf,
	onOpenModal,
	onDecline,
}: IProposalCardProps) => {
	return (
		<Card border="2xl" className="w-full p-5 bg-white shadow-lg">
			<div className="flex justify-between items-center mb-2">
				<div className="w-full flex items-center justify-between md:w-fit lg:w-fit md:items-center lg:items-center">
					<h3 className="text-lg font-normal text-primary-badge">
						{proposal.title}
					</h3>
					<Button
						variant="ghost"
						size="sm"
						className="text-primary"
						onClick={() => onOpenPdf(proposal.pdf)}
					>
						<Badge
							size="xs"
							className="rounded-md bg-purple-200 border-[1.5px] border-purple-200 text-purple-800"
						>
							<div className="text-purple-800 flex flex-row items-center gap-1">
								<span>PDF</span>
								<Icon name="arrow-up-right" className="fill-purple-800" />
							</div>
						</Badge>
					</Button>
				</div>

				{proposal.status !== EStatus.Declined && (
					<>
						<div className="hidden md-lg:block lg:block h-fit px-2 py-1 bg-gray-200 rounded-lg items-center">
							<Checkbox
								label="Select"
								checked={isSelected}
								onChange={() => onSelect(proposal)}
							/>
						</div>
						<div className="block h-fit px-2 py-1 bg-gray-200 rounded-lg items-center md-lg:hidden lg:hidden">
							<Checkbox
								checked={isSelected}
								onChange={() => onSelect(proposal)}
								hideLabel
								aria-label="Select proposal"
							/>
						</div>
					</>
				)}
			</div>
			<p className="text-xs text-gray-600 text-muted-foreground mb-4">
				Date: {proposal.date} | Items: {proposal.quantity}
			</p>
			<div className="p-3 rounded-lg border-[1.5px] border-grayColor shadow-sm md:flex lg:flex-row justify-between items-center bg-gray">
				<div className="flex w-full justify-between space-x-4 xs-sm:flex-col xs-sm:space-x-0 xs-sm:gap-2 xs-sm:items-center lg:justify-start">
					<span className="flex items-baseline gap-2 text-2xl">
						<FormatCurrency value={proposal.total} className="font-dm-sans" />
						<span className="text-xs text-gray-500 font-normal text-muted-foreground">
							Total Price
						</span>
					</span>
					<span className="flex items-baseline gap-2 text-2xl">
						<FormatCurrency value={proposal.deposit} className="font-dm-sans" />
						<span className="text-xs text-gray-500 font-normal text-muted-foreground">
							Deposit
						</span>
					</span>
				</div>
				<div className="w-full flex text-sm mt-2 lg:m-0 flex-row items-center justify-center gap-3 lg:justify-end">
					{proposal.status === EStatus.Opened && (
						<div className="w-full flex flex-col items-center gap-2 md:flex-row lg:flex-row lg:justify-end">
							<Button
								variant="outline"
								className="w-full !bg-[#0a0c30] md:w-1/2 md-lg:w-1/2 lg:w-1/3"
								size="sm"
								onClick={() => onDecline(proposal)}
							>
								<span className="whitespace-nowrap flex text-white items-center">
									<Icon name="x-circle" className="mr-2 fill-white" />
									<span className="text-md md:text-sm lg:text-xs">Decline</span>
								</span>
							</Button>
							<Button
								variant="primary"
								className="w-full md:w-1/2 md-lg:w-1/2 lg:w-1/3"
								size="sm"
								onClick={() => onOpenModal(proposal)}
							>
								<span className="whitespace-nowrap mx-3 flex items-center">
									<Icon name="check-circle" className="mr-2 fill-white" />
									<span className="text-md md:text-sm lg:text-xs">
										Approve & Sign
									</span>
								</span>
							</Button>
						</div>
					)}
					{proposal.status !== 'opened' && (
						<span className="text-sm flex flex-row items-center gap-2">
							<Icon
								name={
									proposal.status === EStatus.Declined
										? 'x-circle'
										: 'check-circle'
								}
							/>
							{proposal.status === EStatus.Declined ? 'Declined' : 'Approved'}
						</span>
					)}
				</div>
			</div>
		</Card>
	);
};
